import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { contractService } from '../../services/contractService';
import { generateContractHTML, validateNIF } from '../../utils/contractTemplate';
import { proposalPdfService } from '../../services/proposalPdfService';
import { supabase } from '../../services/supabaseService';
import type { Contract, ContractSignature } from '../../types/contract';
import type { ProposalContent } from '../../types/proposal-blocks';

export function ContractViewer() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contract, setContract] = useState<Contract | null>(null);
  const [proposalContent, setProposalContent] = useState<ProposalContent | null>(null);
  const [signature, setSignature] = useState<ContractSignature | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  const [form, setForm] = useState({
    name: '', email: '', company: '', nif: '', address: '', phone: '', position: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // ─── Load data ──────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!token) { setError('Token de contrato no valido'); setLoading(false); return; }

    try {
      // La RPC get_contract_by_token devuelve el contrato + prefill (lead/
      // contact linkeados) + signature (si firmado). Antes se hacían 4-5
      // SELECTs directos a proposals/leads/contacts/contract_signatures
      // que ahora están bloqueados por RLS (todo el flujo era anon).
      const c = await contractService.getContractByToken(token) as any;
      if (!c) { setError('Contrato no encontrado'); setLoading(false); return; }
      setContract(c);

      if (c.signature) {
        setSignature({
          contract_id: c.id,
          signature_data_url: c.signature.signature_data_url,
          signed_by_name: c.signature.signed_by_name,
          signed_by_email: c.signature.signed_by_email,
          signed_by_company: c.signature.signed_by_company,
          signed_by_position: c.signature.signed_by_position,
          signed_at: c.signature.signed_at,
        } as any);
      }

      if (c.prefill) {
        setForm(prev => ({
          ...prev,
          name: prev.name || c.prefill.full_name || '',
          email: prev.email || c.prefill.email || '',
          phone: prev.phone || c.prefill.phone || '',
          company: prev.company || c.prefill.company || '',
          position: prev.position || c.prefill.position || '',
          // nif y address NO se pre-rellenan — cliente debe proporcionarlos
        }));
      }

      // proposalContent se muestra en la parte "condiciones" del contrato.
      // Al ser propuesta v2 (bloques) y el editor v1 estar deprecado, no lo
      // cargamos aquí para el visor público — la RPC ya devuelve
      // proposal_title en c.proposalTitle si se quiere mostrar.

      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Canvas drawing (native, no external lib) ───────────────
  const startDraw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    isDrawingRef.current = true;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDraw = () => { isDrawingRef.current = false; };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setErrors((prev) => { const n = { ...prev }; delete n.signature; return n; });
  };

  const isCanvasEmpty = (): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    return !data.some((channel, i) => i % 4 === 3 && channel !== 0);
  };

  // ─── Touch support ─────────────────────────────────────────
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    isDrawingRef.current = true;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    ctx.beginPath();
    ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
    ctx.stroke();
  };

  // ─── Validate ───────────────────────────────────────────────
  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'El nombre es obligatorio';
    if (!form.email.trim()) e.email = 'El email es obligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email no valido';
    if (!form.nif.trim()) e.nif = 'El NIF/CIF es obligatorio';
    else if (!validateNIF(form.nif)) e.nif = 'NIF/CIF no valido';
    if (!form.address.trim()) e.address = 'La direccion es obligatoria';
    if (!acceptedTerms) e.terms = 'Debes aceptar los terminos del contrato';
    if (isCanvasEmpty()) e.signature = 'Debes firmar el contrato';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── Submit ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate() || !contract || !proposalContent) return;

    try {
      setIsSubmitting(true);
      const signatureDataUrl = canvasRef.current?.toDataURL() || '';

      await contractService.submitSignature(contract.token, signatureDataUrl, {
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        nif: form.nif,
        address: form.address,
        phone: form.phone || undefined,
        position: form.position || undefined,
      });

      // Send notification (non-blocking)
      contractService.sendSignedNotification(
        contract,
        form.name,
        form.email,
        proposalContent.meta.project_name || 'Proyecto'
      );

      // Generate and store signed PDF (non-blocking)
      try {
        const updatedContract: Contract = {
          ...contract,
          status: 'signed',
          signed_at: new Date().toISOString(),
          client_data: {
            name: form.name, email: form.email, company: form.company,
            nif: form.nif, address: form.address, phone: form.phone, position: form.position,
          },
        };
        const html = generateContractHTML(updatedContract, proposalContent, signatureDataUrl);
        const pdfBlob = await proposalPdfService.generateSignedPdf({
          title: `Contrato ${contract.contract_number}`,
          content: proposalContent as any,
          acceptedByName: form.name,
          acceptedByEmail: form.email,
          acceptedAt: new Date().toISOString(),
          signatureData: signatureDataUrl,
        });
        const fileName = `contrato-${contract.token}-${Date.now()}.pdf`;
        const { error: uploadError } = await supabase.storage.from('signed-proposals').upload(fileName, pdfBlob, { contentType: 'application/pdf' });
        if (uploadError) throw uploadError;
        // Antes: UPDATE directo a contracts que la nueva RLS bloquea. Ahora
        // pasamos por save_signed_contract_pdf (SECURITY DEFINER, valida token).
        await supabase.rpc('save_signed_contract_pdf', {
          p_token: contract.token,
          p_pdf_path: fileName,
        });
      } catch (pdfErr) {
        console.warn('PDF generation failed, but signature was saved:', pdfErr);
      }

      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al procesar la firma');
      setIsSubmitting(false);
    }
  };

  // ─── Contract HTML for preview ──────────────────────────────
  const getContractHTML = (): string => {
    if (!contract || !proposalContent) return '';
    const signatureUrl = signature?.signature_data_url || undefined;
    const previewContract: Contract = {
      ...contract,
      client_data: {
        name: form.name || signature?.signed_by_name || '',
        email: form.email || signature?.signed_by_email || '',
        company: form.company || signature?.signed_by_company || '',
        nif: form.nif || signature?.signed_by_nif || '',
        address: form.address || signature?.signed_by_address || '',
        phone: form.phone || signature?.signed_by_phone || '',
        position: form.position || signature?.signed_by_position || '',
      },
    };
    return generateContractHTML(previewContract, proposalContent, signatureUrl);
  };

  // ─── Render states ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando contrato...</p>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-white p-8 rounded-2xl shadow-xl">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2" /><line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" /><line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" /></svg>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Contrato no disponible</h1>
          <p className="text-gray-600 mb-6">{error || 'El contrato no existe o ha expirado.'}</p>
        </div>
      </div>
    );
  }

  if (contract.status === 'expired' || new Date(contract.expires_at) < new Date()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-white p-8 rounded-2xl shadow-xl">
          <svg className="w-16 h-16 text-orange-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2" /><line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" /><line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" /></svg>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Contrato Expirado</h1>
          <p className="text-gray-600 mb-6">
            Este contrato expiro el {new Date(contract.expires_at).toLocaleDateString('es-ES')}.
          </p>
          <a href="mailto:info@ceroideas.es" className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
            Contactar con Ceroideas
          </a>
        </div>
      </div>
    );
  }

  if (contract.status === 'signed' && signature) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
            <div className="text-center mb-8">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Contrato Firmado</h1>
              <p className="text-gray-600">Firmado el {new Date(signature.signed_at).toLocaleDateString('es-ES')}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h2 className="font-bold text-lg mb-4">Informacion del Firmante</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-600">Nombre</p><p className="font-semibold">{signature.signed_by_name}</p></div>
                <div><p className="text-sm text-gray-600">Email</p><p className="font-semibold">{signature.signed_by_email}</p></div>
                {signature.signed_by_company && <div><p className="text-sm text-gray-600">Empresa</p><p className="font-semibold">{signature.signed_by_company}</p></div>}
                <div><p className="text-sm text-gray-600">NIF/CIF</p><p className="font-semibold">{signature.signed_by_nif}</p></div>
              </div>
            </div>

            {proposalContent && (
              <div className="border border-gray-200 rounded-lg p-6 max-h-[500px] overflow-y-auto mb-6" dangerouslySetInnerHTML={{ __html: getContractHTML() }} />
            )}
          </div>
          <div className="text-center text-sm text-gray-500">Powered by <a href="https://ceroideas.es" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Ceroideas</a></div>
        </div>
      </div>
    );
  }

  // ─── Signing form ───────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-5xl mx-auto py-8">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center gap-4">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <div>
                <h1 className="text-2xl font-bold text-white">Firma de Contrato</h1>
                <p className="text-blue-100">Contrato {contract.contract_number}</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Info banner */}
            <div className="mb-8 bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
              <p className="text-sm text-blue-800">
                <strong>Importante:</strong> Por favor, completa todos los datos y firma el contrato digitalmente.
                Recibiras una copia por email una vez firmado.
              </p>
            </div>

            {showPreview ? (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">Vista Previa del Contrato</h2>
                  <button onClick={() => setShowPreview(false)} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800">
                    Cerrar vista previa
                  </button>
                </div>
                <div className="border border-gray-300 rounded-lg p-6 max-h-[600px] overflow-y-auto bg-white" dangerouslySetInnerHTML={{ __html: getContractHTML() }} />
              </div>
            ) : (
              <>
                {/* Client data form */}
                <h2 className="text-xl font-bold mb-6">Datos del Firmante</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {([
                    { key: 'name', label: 'Nombre Completo', required: true, placeholder: 'Juan Perez Garcia' },
                    { key: 'email', label: 'Email', required: true, placeholder: 'juan@empresa.com', type: 'email' },
                    { key: 'company', label: 'Empresa', required: false, placeholder: 'Mi Empresa S.L.' },
                    { key: 'nif', label: 'NIF/CIF', required: true, placeholder: '12345678A o B12345678' },
                  ] as const).map(({ key, label, required, placeholder, type }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label} {required && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type={type || 'text'}
                        value={form[key]}
                        onChange={(e) => setForm({ ...form, [key]: key === 'nif' ? e.target.value.toUpperCase() : e.target.value })}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors[key] ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder={placeholder}
                      />
                      {errors[key] && <p className="text-red-500 text-sm mt-1">{errors[key]}</p>}
                    </div>
                  ))}

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Direccion Fiscal <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Calle Principal 123, 28001 Madrid"
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefono</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="600 123 456"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
                    <input
                      type="text"
                      value={form.position}
                      onChange={(e) => setForm({ ...form, position: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Director General"
                    />
                  </div>
                </div>

                {/* Preview button */}
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors mb-8"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  Vista Previa del Contrato
                </button>

                {/* Signature */}
                <div className="border-t pt-8">
                  <h2 className="text-xl font-bold mb-4">Firma Digital</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Por favor, firma en el recuadro de abajo usando tu raton o pantalla tactil:
                  </p>

                  <div className="border-2 border-gray-300 rounded-xl bg-white mb-4">
                    <canvas
                      ref={canvasRef}
                      width={600}
                      height={180}
                      className="w-full cursor-crosshair"
                      style={{ touchAction: 'none' }}
                      onMouseDown={startDraw}
                      onMouseMove={draw}
                      onMouseUp={stopDraw}
                      onMouseLeave={stopDraw}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={stopDraw}
                    />
                  </div>

                  <button onClick={clearSignature} className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors mb-6">
                    Limpiar Firma
                  </button>
                  {errors.signature && <p className="text-red-500 text-sm mb-4">{errors.signature}</p>}

                  {/* Terms */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1" />
                      <span className="text-sm text-gray-700">
                        He leido y acepto todos los terminos y condiciones de este contrato. Confirmo que tengo
                        autoridad para firmar en nombre de {form.company || 'la empresa'} y que todos los
                        datos proporcionados son correctos.
                      </span>
                    </label>
                    {errors.terms && <p className="text-red-500 text-sm mt-2">{errors.terms}</p>}
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        Procesando firma...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        Firmar Contrato
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-sm text-gray-600">
          <p>Powered by <a href="https://ceroideas.es" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Ceroideas</a></p>
        </div>
      </div>
    </div>
  );
}
